import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import ContextualHelp from '@/components/ContextualHelp';
import {
  MessageCircleHeart, BookOpen, Phone, ArrowRight, ShieldAlert, HandHeart, Sparkles, Heart, GraduationCap,
} from 'lucide-react';

// Compact circular progress ring (SVG) for the Support University card.
function ProgressRing({ pct, size = 48, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="su-progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(11,25,44,0.10)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#C5A059" strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-navy font-semibold" style={{ fontSize: size * 0.28 }}>{pct}%</text>
    </svg>
  );
}

// Rotating support tips — deterministic per user + calendar day.
const TIPS = [
  {
    tip: '\u201cHow was your day?\u201d opens 20% of conversations. \u201cWhat\u2019s the hardest thing on your plate right now?\u201d opens 80%.',
    body: 'Specific, non-judgmental questions invite honesty. General questions invite one-word answers.',
  },
  {
    tip: 'Listening is louder than advice.',
    body: 'When he starts talking, resist the urge to fix. Nod. Ask one follow-up question. Let the silence do its work.',
  },
  {
    tip: 'Say what you\u2019ve seen, not what you\u2019ve concluded.',
    body: '\u201cI noticed you\u2019ve been up late this week\u201d lands differently than \u201cI think you\u2019re depressed.\u201d Observations open doors — labels close them.',
  },
  {
    tip: 'You cannot save someone. You can walk alongside them.',
    body: 'That distinction is the difference between healthy support and burnout. Read the Boundaries guide when you have five minutes.',
  },
  {
    tip: 'Take care of yourself first.',
    body: 'The people who support men well have their own support too — a friend, a therapist, a rhythm. Burned-out supporters can\u2019t hold anyone.',
  },
  {
    tip: 'Never promise secrecy about safety.',
    body: '\u201cI love you too much to keep this a secret\u201d is not a betrayal — it\u2019s love with a floor under it.',
  },
  {
    tip: 'Small check-ins beat big rescues.',
    body: 'A two-minute call on a Tuesday means more than a two-hour heart-to-heart during a crisis. Consistency compounds.',
  },
];

function pickTip(uid) {
  const day = new Date().toISOString().slice(0, 10);
  const seed = String(uid || 'anon') + day;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return TIPS[hash % TIPS.length];
}

function greeting(name) {
  const h = new Date().getHours();
  const bucket = h < 5 ? 'Working late' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return `${bucket}, ${name}.`;
}

const HOME_HELP = {
  whatIs: 'Support Home is your daily starting point as a Brotherhood Support member. It surfaces today\u2019s support tip, your quick actions, and crisis resources — in that order of priority.',
  whyMatters: 'Supporting a person you care about is a rhythm, not a project. A simple daily habit — one tip, one small step — makes you a better support without burning you out.',
  howToUse: 'Read today\u2019s tip. Then choose one action: talk through a moment with the Support Coach, browse a guide in the Library, or check the crisis resources if the moment calls for it.',
  tips: [
    'Bookmark this page. Start every visit here.',
    'You don\u2019t have to read everything. One small thing today. One tomorrow.',
    'If you\u2019re worried about immediate safety, jump straight to the red crisis card.',
  ],
};

export default function SupportHome() {
  const { user } = useAuth();
  const firstName = (user?.name || 'friend').split(' ')[0];
  const tip = pickTip(user?.id);
  const [su, setSu] = useState(null);

  useEffect(() => {
    http.get('/support/university/overview').then((r) => setSu(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:px-10 lg:pt-10 pb-24 space-y-8 lg:space-y-10" data-testid="support-home">

      {/* GREETING */}
      <section className="animate-fade-up">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
          <HandHeart size={12} strokeWidth={1.75}/> Brotherhood Support
        </div>
        <h1 className="font-heading text-4xl lg:text-6xl mt-3 lg:mt-4 leading-[1.05] text-navy flex items-start gap-2 flex-wrap" data-testid="support-home-greeting">
          {greeting(firstName)}
          <ContextualHelp pageTitle="Support Home" content={HOME_HELP} testid="support-home-help"/>
        </h1>
        <p className="font-heading text-2xl lg:text-3xl italic text-navy/60 mt-2 lg:mt-3 leading-snug" data-testid="support-home-tagline">
          For the people who help him do it.
        </p>
        <p className="text-navy/70 mt-4 lg:mt-6 text-base lg:text-lg leading-relaxed max-w-3xl">
          Supporting a man you care about — a partner, son, father, brother, mentee, or friend — takes patience, skill, and the right kind of presence.
          Brotherhood Support gives you the language, tools, and reassurance for the moments that matter.
        </p>
      </section>

      {/* TODAY'S TIP */}
      <section
        className="bg-navy text-white rounded-3xl p-8 lg:p-10 card-soft relative overflow-hidden"
        data-testid="support-home-tip"
      >
        <div className="absolute -right-8 -top-8 opacity-10">
          <Sparkles size={180} strokeWidth={0.6} className="text-gold"/>
        </div>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-semibold relative">Today&apos;s support tip</p>
        <p className="font-heading text-2xl lg:text-3xl mt-3 lg:mt-4 leading-snug relative">&ldquo;{tip.tip}&rdquo;</p>
        <p className="text-white/70 text-sm lg:text-base mt-4 leading-relaxed max-w-3xl relative">{tip.body}</p>
      </section>

      {/* QUICK ACTIONS */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.24em] text-navy/50 font-semibold mb-4">What would you like to do?</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5" data-testid="support-home-actions">
          <Link
            to="/app/support-university"
            data-testid="support-home-goto-university"
            className="group bg-white rounded-3xl p-6 lg:p-7 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
                <GraduationCap size={22} className="text-gold" strokeWidth={1.5}/>
              </div>
              {su && su.lessons_total > 0 && su.lessons_completed > 0 ? <ProgressRing pct={su.pct} /> : null}
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mt-4">Learn</p>
            <p className="font-heading text-xl text-navy mt-1 leading-snug">Support University</p>
            <p className="text-sm text-navy/60 mt-2 leading-relaxed flex-1">
              Clinician-informed courses tailored to who you support — listening, hard conversations, crisis response, and more.
            </p>
            {su && su.lessons_completed > 0 ? (
              <p className="text-xs text-navy/50 mt-3" data-testid="su-home-progress">
                {su.pct}% complete · {su.courses_completed} of {su.courses_total} courses finished
              </p>
            ) : null}
            <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-navy group-hover:text-gold transition-smooth">
              {su && su.lessons_completed > 0 ? 'Continue learning' : 'Start learning'} <ArrowRight size={12}/>
            </span>
          </Link>

          <Link
            to="/app/support-university?tab=guides"
            data-testid="support-home-goto-library"
            className="group bg-white rounded-3xl p-6 lg:p-7 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col"
          >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
              <BookOpen size={22} className="text-gold" strokeWidth={1.5}/>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mt-4">Explore</p>
            <p className="font-heading text-xl text-navy mt-1 leading-snug">Support Library</p>
            <p className="text-sm text-navy/60 mt-2 leading-relaxed flex-1">
              Practical guides on difficult conversations, therapy resistance, grief, anger, boundaries, and self-care.
            </p>
            <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-navy group-hover:text-gold transition-smooth">
              Browse guides <ArrowRight size={12}/>
            </span>
          </Link>

          <Link
            to="/app/support-coach"
            data-testid="support-home-goto-coach"
            className="group bg-navy text-white rounded-3xl p-6 lg:p-7 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col"
          >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.22)' }}>
              <MessageCircleHeart size={22} className="text-gold" strokeWidth={1.5}/>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mt-4">Talk it through</p>
            <p className="font-heading text-xl mt-1 leading-snug">Support Coach</p>
            <p className="text-sm text-white/75 mt-2 leading-relaxed flex-1">
              Warm, evidence-informed AI coaching for the exact moment you&apos;re in. Never diagnoses. Never controls.
            </p>
            <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-gold">
              Start a conversation <ArrowRight size={12}/>
            </span>
          </Link>

          <Link
            to="/app/support-crisis"
            data-testid="support-home-goto-crisis"
            className="group bg-white rounded-3xl p-6 lg:p-7 border border-red-100 hover:-translate-y-0.5 transition-smooth flex flex-col"
          >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(220,38,38,0.10)' }}>
              <ShieldAlert size={22} className="text-red-600" strokeWidth={1.5}/>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-red-600 font-semibold mt-4">If you&rsquo;re worried</p>
            <p className="font-heading text-xl text-navy mt-1 leading-snug">Crisis Resources</p>
            <p className="text-sm text-navy/60 mt-2 leading-relaxed flex-1">
              911, 988 Suicide & Crisis Lifeline, veterans line, and safety planning language you can use right now.
            </p>
            <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-red-600">
              View resources <ArrowRight size={12}/>
            </span>
          </Link>
        </div>
      </section>

      {/* GENTLE CLOSER */}
      <section
        className="bg-beige rounded-3xl p-6 lg:p-8 card-soft flex items-start gap-4"
        data-testid="support-home-closer"
      >
        <Heart size={22} className="text-gold shrink-0 mt-1" strokeWidth={1.5}/>
        <div>
          <p className="font-heading text-lg lg:text-xl text-navy">Supporting someone is not about having all the answers.</p>
          <p className="text-sm lg:text-base text-navy/70 mt-2 leading-relaxed max-w-3xl">
            It&rsquo;s about showing up with compassion, patience, honesty, and healthy boundaries. Brotherhood Support is here whenever you need guidance.
          </p>
        </div>
      </section>

      {/* CRISIS QUICK LINE */}
      <p className="text-center text-xs text-navy/50 leading-relaxed max-w-2xl mx-auto">
        In immediate danger, call <a href="tel:911" className="underline underline-offset-2 text-navy">911</a>.
        Otherwise, call or text <a href="tel:988" className="underline underline-offset-2 text-navy">988</a> — free, confidential, 24/7.
        Educational content only. Not a substitute for therapy, medical care, or emergency services.
      </p>

      {/* small visual hook to bottom-left phone icon — reinforces crisis path */}
      <div className="hidden">
        <Phone/>
      </div>
    </div>
  );
}
